<template>
  <div :class="[{ purpose: this.data?.body?.purpose }]">
    <q-form ref="form" @submit="this.onSubmit">
      <div class="form-section">
        <h3 class="form-section__title">Основная информация</h3>
        <div class="form">
          <div class="form__item form__item--full" ref="contractor_name_ref">
            <CustomInput
              ref="contractor_name_input"
              label="Наименование"
              type="text"
              :required="true"
              :value="this.formData.contractor_name"
              @change_input="(value) => onChange('contractor_name', value)"
            />
          </div>

          <div class="form__item" ref="legal_name_ref">
            <CustomInput
              ref="legal_name_input"
              label="Юридическое наименование"
              type="text"
              :required="true"
              :value="this.formData.legal_name"
              @change_input="(value) => onChange('legal_name', value)"
            />
          </div>

          <div class="form__item" ref="bin_ref">
            <CustomInput
              ref="bin_input"
              label="БИН"
              type="text"
              :required="true"
              :value="this.formData.bin"
              @change_input="(value) => onChange('bin', value)"
            />
          </div>

          <div class="form__item" ref="director_fio_ref">
            <CustomInput
              ref="director_fio_input"
              label="Полное ФИО Директора"
              type="text"
              :value="this.formData.director_fio"
              @change_input="(value) => onChange('director_fio', value)"
              :required="true"
            />
          </div>

          <div class="form__item" ref="contract_text_type_id_ref">
            <CustomSelect
              ref="contract_text_type_id_input"
              :options="contractTextTypeOptionsWithCurrent"
              label="Действующий на основании"
              optionValueKey="contract_text_type_id"
              optionLabelKey="contract_text_type_name"
              :value="this.formData.contract_text_type_id || ''"
              @change="(value) => onContractTextTypeChange(value)"
              :required="true"
            />
          </div>

          <template v-if="showActingBasisDocumentFields">
            <div class="form__item">
              <CustomInput
                :label="actingBasisDocumentNumberLabel"
                type="text"
                :value="this.formData.acting_basis_document_number"
                placeholder="Например: БН-123"
                @change_input="(value) => onChange('acting_basis_document_number', value)"
              />
            </div>

            <div class="form__item">
              <CustomDate
                :fullWidth="true"
                :label="actingBasisDocumentDateLabel"
                :value="this.formData.acting_basis_document_date"
                placeholder="Например: 04.09.2025"
                @change_date="(value) => onChange('acting_basis_document_date', value)"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="form-section">
        <h3 class="form-section__title">Интеграция с SBS</h3>
        <div class="form">
          <div class="form__item">
            <CustomInputDropdown
              :options="contragentSBSOptions"
              label="Заказчик SBS (поиск по БИН или по наименованию)"
              @change_input="(val) => this.contragentSBSFilterFunc(val)"
              optionValueKey="sbs_company_id"
              optionLabelKey="naimenovanie"
              :value="this.formData.sbs_company_name || ''"
              :loading="this.contragentSBSLoading"
              @select_item="this.contragentSBSSelectItem"
            />
          </div>

          <div class="form__item">
            <CustomInputDropdown
              :options="partnerSBSOptions"
              label="Подрядчик SBS (поиск по БИН или по наименованию)"
              optionValueKey="sbs_partner_id"
              optionLabelKey="naimenovanie"
              :value="this.formData.sbs_partner_name || ''"
              :loading="this.partnerSBSLoading"
              @change_input="(val) => this.partnerSBSFilterFunc(val)"
              @select_item="this.partnerSBSSelectItem"
            />
          </div>

          <div class="form__item" ref="sbs_snr_id_ref">
            <CustomSelect
              ref="sbs_snr_id_input"
              :options="sbsSnrOptions"
              label="Режим налогообложения (СНР)"
              optionValueKey="sbs_spetsialnye_nalogovye_rezhimy_id"
              optionLabelKey="naimenovanie"
              :value="this.formData.sbs_snr_id || ''"
              @change="(value) => onChange('sbs_snr_id', value)"
              :required="false"
              :placeholder="this.snrPlaceholder"
            />
            <div v-if="showNoSnrWarning" class="okedy-warning-text">
              У Подрядчика SBS нет СНР
            </div>
          </div>

          <div class="form__item" ref="sbs_vidy_deyatelnosti_id_ref">
            <CustomSelect
              ref="sbs_vidy_deyatelnosti_id_input"
              :options="availableOkedyOptionsForSelect"
              label="ОКЕД (вид деятельности)"
              :filterFunc="showMultipleOkedyWarning ? null : this.sbsVidyDeyatelnostiFilterFunc"
              optionValueKey="sbs_vidy_deyatelnosti_id"
              optionLabelKey="display_name"
              :value="this.formData.sbs_vidy_deyatelnosti_id || ''"
              :loading="this.sbsVidyDeyatelnostiLoading"
              @change="this.sbsVidyDeyatelnostiChange"
              :required="false"
              :placeholder="this.okedyPlaceholder"
            />
            <div v-if="showMultipleOkedyWarning" class="okedy-warning-text">
              У Подрядчика SBS указано несколько видов деятельности. Выберите одну из них.
            </div>
            <div v-if="showNoOkedWarning" class="okedy-warning-text">
              У Подрядчика SBS нет ОКЕД
            </div>
          </div>

          <div class="form__item">
            <CustomInput
              label="Комментарий по СНР"
              type="textarea"
              :value="this.formData.sbs_avto_link_comment_snr"
              :disable="true"
              placeholder=" "
            />
          </div>

          <div class="form__item">
            <CustomInput
              label="Комментарий по видам деятельности"
              type="textarea"
              :value="this.formData.sbs_avto_link_comment_vidy_deyatelnosti"
              :disable="true"
              placeholder=" "
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="form-section__title">Банковские реквизиты</h3>
        <div class="form">
          <div class="form__item" ref="iik_ref">
            <CustomInput
              ref="iik_input"
              label="ИИК"
              type="text"
              :value="this.formData.iik"
              @change_input="(value) => onChange('iik', value)"
              :required="true"
            />
          </div>

          <div class="form__item" ref="account_name_ref">
            <CustomInput
              ref="account_name_input"
              label="Наименование банка"
              type="text"
              :value="this.formData.account_name"
              @change_input="(value) => onChange('account_name', value)"
              :required="true"
            />
          </div>

          <div class="form__item" ref="bik_ref">
            <CustomInput
              ref="bik_input"
              label="БИК"
              type="text"
              :value="this.formData.bik"
              @change_input="(value) => onChange('bik', value)"
              :required="true"
            />
          </div>

          <div class="form__item" ref="kbe_ref">
            <CustomInput
              ref="kbe_input"
              label="КБе"
              type="number"
              :value="this.formData.kbe"
              @change_input="(value) => onChange('kbe', value)"
              :required="true"
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="form-section__title">Адрес и локация</h3>
        <div class="form">
          <div class="form__item" ref="address_ref">
            <CustomInput
              ref="address_input"
              label="Юридический Адрес"
              type="textarea"
              :required="true"
              :value="this.formData.address"
              @change_input="(value) => onChange('address', value)"
            />
          </div>

          <div class="form__item" ref="city_id_ref">
            <CustomSelect
              ref="city_id_input"
              :options="cityOptions"
              label="Бизнес регион"
              :required="true"
              optionValueKey="city_id"
              optionLabelKey="city_name"
              :value="this.formData.city_id || ''"
              @change="(value) => onChange('city_id', value)"
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="form-section__title">Типы и классификация</h3>
        <div class="form">
          <div class="form__item" ref="contractor_type_id_ref">
            <CustomSelect
              ref="contractor_type_id_input"
              :options="contractorTypeOptions"
              label="Тип подрядчика"
              :required="true"
              optionValueKey="contractor_type_id"
              optionLabelKey="contractor_type_name"
              :value="this.formData.contractor_type_id || ''"
              @change="(value) => onChange('contractor_type_id', value)"
            />
          </div>

          <div class="form__item" ref="contragent_type_id_ref">
            <CustomSelect
              ref="contragent_type_id_input"
              :options="contragentTypeOptions"
              label="Тип контрагента"
              :required="true"
              optionValueKey="contragent_type_id"
              optionLabelKey="contragent_type_name"
              :value="this.formData.contragent_type_id || ''"
              @change="(value) => onChange('contragent_type_id', value)"
            />
          </div>

          <div class="form__item" ref="is_nds_ref">
            <CustomSelect
              ref="is_nds_input"
              :options="isNdsOptions"
              label="НДС"
              optionValueKey="value"
              optionLabelKey="label"
              :value="this.formData.is_nds"
              @change="(value) => onChange('is_nds', value)"
              :required="true"
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="form-section__title">Контактная информация</h3>
        <div class="form">
          <div class="form__item" ref="phone_ref">
            <CustomInput
              ref="phone_input"
              label="Телефон"
              type="text"
              :required="true"
              :value="this.formData.phone"
              @change_input="(value) => onChange('phone', value)"
            />
          </div>

          <div class="form__item" ref="email_ref">
            <CustomInput
              ref="email_input"
              label="Email"
              type="email"
              :value="this.formData.email"
              @change_input="(value) => onChange('email', value)"
            />
          </div>

          <div class="form__item form__item--full">
            <CustomInput
              label="Контакты"
              type="textarea"
              :value="this.formData.contacts"
              @change_input="(value) => onChange('contacts', value)"
              :required="false"
            />
          </div>
        </div>
      </div>

      <CustomModalFooter :btn-loading="this.loading" />
    </q-form>
  </div>
</template>

<script>
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomDate from "@/components/form/CustomDate";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomInputDropdown from "@/components/form/CustomInputDropdown";
import CustomUploader from "@/components/form/CustomUploader";
import { getCity, getTaxopark, getContractorType, getContragentType, getContractorMainType, mapSBSCompany, mapSBSPartner, getContractTextType } from "@/views/Contractors/services";
import { mapSBSVidyDeyatelnosti, getSBSsnr } from "@/views/Providers/services";

export default {
  name: "add-handbook-contractor",
  components: { CustomInput, CustomSelect, CustomInputDropdown, CustomModalFooter, CustomDate, CustomCheckbox, CustomUploader },
  props: ['data'],
  data() {
    return {
      loading: false,
      formData: {
        contractor_name: this.data?.body?.contractor_name || '',
        director_fio: this.data?.body?.director_fio_myspace || '',
        legal_name: this.data?.body?.legal_name || '',
        guid: this.data?.body?.guid || '',
        bin: this.data?.body?.bin || '',
        address: this.data?.body?.address || '',
        city_id: this.data?.body?.city_id || '',
        taxopark_id: this.data?.body?.taxopark_id || '',
        contractor_type_id: this.data?.body?.contractor_type_id || '',
        contragent_type_id: this.data?.body?.contragent_type_id || '',
        contacts: this.data?.body?.contacts || '',
        iik: this.data?.body?.iik || '',
        account_name: this.data?.body?.account_name || '',
        bik: this.data?.body?.bik || '',
        kbe: this.data?.body?.kbe || '',
        contract_text: this.data?.body?.contract_text || '',
        contract_text_type_id: this.data?.body?.contract_text_type_id || '',
        acting_basis_document_number: this.data?.body?.acting_basis_document_number || '',
        acting_basis_document_date: this.data?.body?.acting_basis_document_date || '',
        contractor_limit: this.data?.body?.contractor_limit || '',
        accession_contract_num: this.data?.body?.accession_contract_num || '',
        accession_contract_date: this.data?.body?.accession_contract_date || '',
        document: '',
        is_signed_accession_contract: this.data?.body?.is_signed_accession_contract || 0,
        phone: this.data?.body?.phone || '',
        email: this.data?.body?.email || '',
        contractor_code: this.data?.body?.contractor_code || '',
        contractor_main_id: this.data?.body?.contractor_main_id || '',
        sbs_company_id: this.data?.body?.sbs_company_id || '',
        sbs_partner_id: this.data?.body?.sbs_partner_id || '',
        sbs_partner_name: this.data?.body?.sbs_partner_name || '',
        sbs_company_name: this.data?.body?.sbs_company_name || '',
        is_nds: this.data?.body?.is_nds !== undefined ? this.data?.body?.is_nds : null,
        agreement_postavka_guid: this.data?.body?.agreement_postavka_guid || '',
        sbs_vidy_deyatelnosti_id: this.data?.body?.sbs_vidy_deyatelnosti_id || '',
        display_name: this.data?.body?.display_name || '',
        sbs_snr_id: this.data?.body?.sbs_snr_id || '',
        naimenovanie: this.data?.body?.naimenovanie || '',
        sbs_avto_link_comment_vidy_deyatelnosti: this.data?.body?.sbs_avto_link_comment_vidy_deyatelnosti || '',
        sbs_avto_link_comment_snr: this.data?.body?.sbs_avto_link_comment_snr || '',
      },
      cityOptions: [],
      taxoparkOptions: [],
      contractorTypeOptions: [],
      contragentTypeOptions: [],
      partnerSBSOptions: [],
      contragentSBSOptions: [],
      contractorMainTypeOptions: [],
      contragentSBSLoading: false,
      partnerSBSLoading: false,
      sbsCompanySearchTimeout: null,
      sbsPartnerSearchTimeout: null,
      sbsVidyDeyatelnostiOptions: [],
      sbsVidyDeyatelnostiLoading: false,
      showMultipleOkedyWarning: false,
      showNoSnrWarning: false,
      showNoOkedWarning: false,
      availableOkedyOptionsFromPartner: [], // Варианты видов деятельности, полученные от выбранного подрядчика
      hasMultipleOkedyFromPartner: false, // Флаг, что у выбранного подрядчика несколько видов деятельности
      isNdsOptions: [
        { label: 'Не выбрано', value: null },
        { label: 'НДС', value: true },
        { label: 'Без НДС', value: false },
      ],
      sbsSnrOptions: [],
      contractTextTypeOptions: [],
    }
  },
  computed: {
    // Добавляем текущее значение в список опций, если его там нет
    contractTextTypeOptionsWithCurrent() {
      const options = [...this.contractTextTypeOptions];

      // Если есть contract_text и он не найден в списке опций, добавляем его
      if (this.formData.contract_text && !this.isContractTextInOptions) {
        // Создаем временную опцию для текущего значения
        // Используем отрицательный ID, чтобы отличить от реальных опций
        const tempOption = {
          contract_text_type_id: `temp_${this.formData.contract_text}`,
          contract_text_type_name: this.formData.contract_text,
          isTemporary: true
        };
        options.unshift(tempOption);
      }

      return options;
    },
    // Проверяем, найден ли текущий contract_text в списке опций
    isContractTextInOptions() {
      if (!this.formData.contract_text || !this.contractTextTypeOptions.length) {
        return false;
      }
      return this.contractTextTypeOptions.some(
        option => option.contract_text_type_name === this.formData.contract_text
      );
    },
    // Выбранная опция "Действующий на основании"
    selectedContractTextType() {
      if (!this.formData.contract_text_type_id) return null;
      return this.contractTextTypeOptionsWithCurrent.find(
        opt => opt.contract_text_type_id === this.formData.contract_text_type_id ||
          String(opt.contract_text_type_id) === String(this.formData.contract_text_type_id)
      );
    },
    // Показывать ли поля номера и даты (для доверенности/талона)
    showActingBasisDocumentFields() {
      const name = this.selectedContractTextType?.contract_text_type_name?.toLowerCase() || '';
      return name.includes('доверенност') || name.includes('талон');
    },
    // Подпись для поля номера
    actingBasisDocumentNumberLabel() {
      const name = this.selectedContractTextType?.contract_text_type_name?.toLowerCase() || '';
      return name.includes('талон') ? 'Номер талона' : 'Номер доверенности';
    },
    // Подпись для поля даты
    actingBasisDocumentDateLabel() {
      const name = this.selectedContractTextType?.contract_text_type_name?.toLowerCase() || '';
      return name.includes('талон') ? 'Дата талона' : 'Дата доверенности';
    },
    // Placeholder для поля "Режим налогообложения (СНР)"
    snrPlaceholder() {
      if (!this.formData.sbs_snr_id && this.formData.sbs_avto_link_comment_snr) {
        return this.formData.sbs_avto_link_comment_snr;
      }
      return 'Выберите..';
    },
    // Placeholder для поля "ОКЕД (вид деятельности)"
    okedyPlaceholder() {
      if (!this.formData.sbs_vidy_deyatelnosti_id && this.formData.sbs_avto_link_comment_vidy_deyatelnosti) {
        return this.formData.sbs_avto_link_comment_vidy_deyatelnosti;
      }
      return 'Выберите..';
    },
    // Опции для выбора вида деятельности - ограниченные или полные
    availableOkedyOptionsForSelect() {
      // Если у выбранного подрядчика несколько видов деятельности, показываем только варианты из API
      if (this.hasMultipleOkedyFromPartner && this.availableOkedyOptionsFromPartner.length > 0) {
        // Если есть выбранное значение, убеждаемся, что оно есть в списке опций
        if (this.formData.sbs_vidy_deyatelnosti_id && this.formData.display_name) {
          const selectedExists = this.availableOkedyOptionsFromPartner.some(
            opt => opt.sbs_vidy_deyatelnosti_id === this.formData.sbs_vidy_deyatelnosti_id
          );
          
          // Если выбранное значение не найдено в ограниченном списке, добавляем его
          if (!selectedExists) {
            return [
              {
                sbs_vidy_deyatelnosti_id: this.formData.sbs_vidy_deyatelnosti_id,
                display_name: this.formData.display_name
              },
              ...this.availableOkedyOptionsFromPartner
            ];
          }
        }
        return this.availableOkedyOptionsFromPartner;
      }
      // Иначе показываем полный список
      return this.sbsVidyDeyatelnostiOptions;
    }
  },
  methods: {
    async initSbsFromLinkedPartner() {
      // Если уже связан sbs_partner_id, но поля СНР/ОКЭД не выбраны — подтянем варианты и ограничим список
      if (!this.formData.sbs_partner_id) return;
      const needSnr = !this.formData.sbs_snr_id;
      const needOked = !this.formData.sbs_vidy_deyatelnosti_id;
      if (!needSnr && !needOked) return;

      try {
        this.partnerSBSLoading = true;
        const response = await mapSBSPartner({ search_text: this.formData.sbs_partner_name || '' });
        const list = Array.isArray(response) ? response : [];
        const found = list.find(it => String(it.sbs_partner_id) === String(this.formData.sbs_partner_id));
        if (found) {
          // СНР
          if (needSnr) {
            if (found.sbs_snr_id) {
              this.formData.sbs_snr_id = found.sbs_snr_id;
              const exists = this.sbsSnrOptions.some(opt => opt.sbs_spetsialnye_nalogovye_rezhimy_id === found.sbs_snr_id);
              if (!exists && found.sbs_snr_name) {
                this.sbsSnrOptions.push({
                  sbs_spetsialnye_nalogovye_rezhimy_id: found.sbs_snr_id,
                  naimenovanie: found.sbs_snr_name
                });
              }
              this.showNoSnrWarning = false;
            } else {
              this.showNoSnrWarning = true;
            }
          }

          // ОКЭД
          if (needOked) {
            if (found.sbs_vidy_deyatelnosti_id && Array.isArray(found.sbs_vidy_deyatelnosti_id) && found.sbs_vidy_deyatelnosti_id.length > 0) {
              if (found.sbs_vidy_deyatelnosti_id.length === 1) {
                const vidId = found.sbs_vidy_deyatelnosti_id[0];
                const vidName = found.vidy_deyatelnosti_name && found.vidy_deyatelnosti_name[0] ? found.vidy_deyatelnosti_name[0] : '';
                this.formData.sbs_vidy_deyatelnosti_id = vidId;
                this.formData.display_name = vidName;
                const exists = this.sbsVidyDeyatelnostiOptions.some(opt => opt.sbs_vidy_deyatelnosti_id === vidId);
                if (!exists && vidName) {
                  this.sbsVidyDeyatelnostiOptions.unshift({
                    sbs_vidy_deyatelnosti_id: vidId,
                    display_name: vidName,
                    guid_oked_for_onec: found.vidy_deyatelnosti_guid && found.vidy_deyatelnosti_guid[0] ? found.vidy_deyatelnosti_guid[0] : null
                  });
                }
                this.showMultipleOkedyWarning = false;
                this.availableOkedyOptionsFromPartner = [];
                this.hasMultipleOkedyFromPartner = false;
              } else {
                this.formData.sbs_vidy_deyatelnosti_id = '';
                this.formData.display_name = '';
                this.showMultipleOkedyWarning = true;
                this.hasMultipleOkedyFromPartner = true;
                this.availableOkedyOptionsFromPartner = found.sbs_vidy_deyatelnosti_id.map((id, idx) => ({
                  sbs_vidy_deyatelnosti_id: id,
                  display_name: found.vidy_deyatelnosti_name && found.vidy_deyatelnosti_name[idx] ? found.vidy_deyatelnosti_name[idx] : '',
                  guid_oked_for_onec: found.vidy_deyatelnosti_guid && found.vidy_deyatelnosti_guid[idx] ? found.vidy_deyatelnosti_guid[idx] : null
                })).filter(opt => opt.display_name);
              }
              this.showNoOkedWarning = false;
            } else {
              this.showNoOkedWarning = true;
            }
          }
        }
      } finally {
        this.partnerSBSLoading = false;
      }
    },
    contragentSBSSelectItem(value, obj) {
      if (!obj || obj === null || obj === undefined) {
        this.formData = {
          ...this.formData,
          sbs_company_id: null,
          sbs_company_name: ''
        };
      } else {
        this.formData = {
          ...this.formData,
          sbs_company_id: obj.sbs_company_id,
          sbs_company_name: obj.naimenovanie
        };
      }
    },
    async partnerSBSSelectItem(value, obj) {
      if (!obj || obj === null || obj === undefined) {
        this.formData = {
          ...this.formData,
          sbs_partner_id: null,
          sbs_partner_name: '',
          // Очищаем поля при снятии выбора
          sbs_snr_id: '',
          sbs_vidy_deyatelnosti_id: '',
          display_name: ''
        };
        // Скрываем предупреждение при снятии выбора
        this.showMultipleOkedyWarning = false;
        this.showNoSnrWarning = false;
        this.showNoOkedWarning = false;
        // Очищаем список вариантов из API
        this.availableOkedyOptionsFromPartner = [];
        // Сбрасываем флаг
        this.hasMultipleOkedyFromPartner = false;
      } else {
        // Обновляем данные подрядчика
        this.formData = {
          ...this.formData,
          sbs_partner_id: obj.sbs_partner_id,
          sbs_partner_name: obj.naimenovanie
        };
        this.showNoSnrWarning = false;
        this.showNoOkedWarning = false;
        
        // Автоматически заполняем режим налогообложения, если он пришел из API
        if (obj.sbs_snr_id) {
          this.formData.sbs_snr_id = obj.sbs_snr_id;
          
          // Проверяем, есть ли выбранный режим налогообложения в списке опций
          const snrExists = this.sbsSnrOptions.some(
            opt => opt.sbs_spetsialnye_nalogovye_rezhimy_id === obj.sbs_snr_id
          );
          
          // Если режима налогообложения нет в списке опций, добавляем его
          if (!snrExists && obj.sbs_snr_name) {
            this.sbsSnrOptions.push({
              sbs_spetsialnye_nalogovye_rezhimy_id: obj.sbs_snr_id,
              naimenovanie: obj.sbs_snr_name
            });
          }
        } else {
          this.showNoSnrWarning = true;
        }
        
        // Автоматически заполняем вид деятельности, если он пришел из API
        if (obj.sbs_vidy_deyatelnosti_id && Array.isArray(obj.sbs_vidy_deyatelnosti_id) && obj.sbs_vidy_deyatelnosti_id.length > 0) {
          // Если только один вид деятельности - выбираем автоматически
          if (obj.sbs_vidy_deyatelnosti_id.length === 1) {
            const vidDeyatId = obj.sbs_vidy_deyatelnosti_id[0];
            const vidDeyatName = obj.vidy_deyatelnosti_name && obj.vidy_deyatelnosti_name[0] 
              ? obj.vidy_deyatelnosti_name[0] 
              : '';
            
            this.formData.sbs_vidy_deyatelnosti_id = vidDeyatId;
            this.formData.display_name = vidDeyatName;
            
            // Проверяем, есть ли выбранный вид деятельности в списке опций
            const exists = this.sbsVidyDeyatelnostiOptions.some(
              opt => opt.sbs_vidy_deyatelnosti_id === vidDeyatId
            );
            
            // Если вида деятельности нет в списке опций, добавляем его
            if (!exists && vidDeyatName) {
              this.sbsVidyDeyatelnostiOptions.unshift({
                sbs_vidy_deyatelnosti_id: vidDeyatId,
                display_name: vidDeyatName,
                guid_oked_for_onec: obj.vidy_deyatelnosti_guid && obj.vidy_deyatelnosti_guid[0] 
                  ? obj.vidy_deyatelnosti_guid[0] 
                  : null
              });
            }
            // Очищаем список вариантов из API, так как выбран один вариант
            this.availableOkedyOptionsFromPartner = [];
            this.hasMultipleOkedyFromPartner = false;
          } else {
            // Если несколько видов деятельности - НЕ выбираем автоматически, оставляем поле пустым
            // Пользователь должен выбрать один из вариантов вручную
            this.formData.sbs_vidy_deyatelnosti_id = '';
            this.formData.display_name = '';
            
            // Показываем предупреждение о необходимости выбора
            this.showMultipleOkedyWarning = true;
            // Устанавливаем флаг, что у подрядчика несколько видов деятельности
            this.hasMultipleOkedyFromPartner = true;
            
            // Сохраняем только варианты из API в отдельный список для ограниченного выбора
            this.availableOkedyOptionsFromPartner = obj.sbs_vidy_deyatelnosti_id.map((id, index) => ({
              sbs_vidy_deyatelnosti_id: id,
              display_name: obj.vidy_deyatelnosti_name && obj.vidy_deyatelnosti_name[index] 
                ? obj.vidy_deyatelnosti_name[index] 
                : '',
              guid_oked_for_onec: obj.vidy_deyatelnosti_guid && obj.vidy_deyatelnosti_guid[index] 
                ? obj.vidy_deyatelnosti_guid[index] 
                : null
            })).filter(opt => opt.display_name); // Фильтруем только те, у которых есть название
          }
        } else {
          this.showNoOkedWarning = true;
        }
      }
    },
    onChange(key, value) {
      this.formData[key] = value;
    },
    onContractTextTypeChange(value) {
      this.formData.contract_text_type_id = value;
      // При выборе "Устава" очищаем поля доверенности/талона
      const selectedOption = this.contractTextTypeOptionsWithCurrent.find(
        opt => opt.contract_text_type_id === value || String(opt.contract_text_type_id) === String(value)
      );
      const name = selectedOption?.contract_text_type_name?.toLowerCase() || '';
      if (!name.includes('доверенност') && !name.includes('талон')) {
        this.formData.acting_basis_document_number = '';
        this.formData.acting_basis_document_date = '';
      }
    },
    validateRequiredFields() {
      const requiredFields = [
        { key: 'contractor_name', ref: 'contractor_name_ref' },
        { key: 'legal_name', ref: 'legal_name_ref' },
        { key: 'bin', ref: 'bin_ref' },
        { key: 'director_fio', ref: 'director_fio_ref' },
        { key: 'contract_text_type_id', ref: 'contract_text_type_id_ref' },
        { key: 'sbs_snr_id', ref: 'sbs_snr_id_ref' },
        { key: 'sbs_vidy_deyatelnosti_id', ref: 'sbs_vidy_deyatelnosti_id_ref' },
        { key: 'address', ref: 'address_ref' },
        { key: 'city_id', ref: 'city_id_ref' },
        { key: 'contractor_type_id', ref: 'contractor_type_id_ref' },
        { key: 'contragent_type_id', ref: 'contragent_type_id_ref' },
        { key: 'is_nds', ref: 'is_nds_ref' },
        { key: 'phone', ref: 'phone_ref' },
        { key: 'iik', ref: 'iik_ref' },
        { key: 'account_name', ref: 'account_name_ref' },
        { key: 'bik', ref: 'bik_ref' },
        { key: 'kbe', ref: 'kbe_ref' },
      ];

      for (const field of requiredFields) {
        const value = this.formData[field.key];
        const isEmpty = value === null || value === undefined || value === '' || 
                       (typeof value === 'string' && value.trim() === '');
        
        if (isEmpty) {
          // Прокручиваем к незаполненному полю
          this.$nextTick(() => {
            const refElement = this.$refs[field.ref];
            let elementToScroll = null;
            
            if (refElement) {
              // Если это массив, берем первый элемент
              const element = Array.isArray(refElement) ? refElement[0] : refElement;
              
              // Пытаемся получить DOM элемент разными способами
              if (element && element.$el) {
                elementToScroll = element.$el;
              } else if (element && element instanceof HTMLElement) {
                elementToScroll = element;
              } else if (element && element.$el && element.$el.$el) {
                elementToScroll = element.$el.$el;
              }
              
              if (elementToScroll) {
                // Для выпадающих списков находим сам элемент .q-select или .q-field
                const qSelect = elementToScroll.querySelector('.q-select');
                const qField = elementToScroll.querySelector('.q-field');
                const targetElement = qSelect || qField || elementToScroll;
                
                // Находим прокручиваемый контейнер модального окна
                let scrollContainer = null;
                
                // Ищем через closest от целевого элемента
                scrollContainer = targetElement.closest('.scrollbar__wrapper') ||
                                 targetElement.closest('.modal__wrapper') ||
                                 targetElement.closest('.q-card');
                
                // Если не нашли, ищем через document.querySelector
                if (!scrollContainer) {
                  scrollContainer = document.querySelector('.q-dialog .scrollbar__wrapper') ||
                                   document.querySelector('.q-dialog .modal__wrapper') ||
                                   document.querySelector('.q-dialog .q-card');
                }
                
                // Если все еще не нашли, ищем любой прокручиваемый родитель
                if (!scrollContainer) {
                  let parent = targetElement.parentElement;
                  while (parent && parent !== document.body) {
                    const style = window.getComputedStyle(parent);
                    const hasScroll = parent.scrollHeight > parent.clientHeight;
                    if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && hasScroll) {
                      scrollContainer = parent;
                      break;
                    }
                    parent = parent.parentElement;
                  }
                }
                
                if (scrollContainer) {
                  // Получаем позиции элементов
                  const containerRect = scrollContainer.getBoundingClientRect();
                  const targetRect = targetElement.getBoundingClientRect();
                  
                  // Вычисляем позицию элемента относительно контейнера
                  const elementTopRelativeToContainer = targetRect.top - containerRect.top + scrollContainer.scrollTop;
                  
                  // Вычисляем желаемую позицию (с отступом сверху)
                  const offset = 80; // Отступ сверху в пикселях
                  const desiredScrollTop = Math.max(0, elementTopRelativeToContainer - offset);
                  
                  // Прокручиваем контейнер вручную с плавной анимацией
                  const startScrollTop = scrollContainer.scrollTop;
                  const distance = desiredScrollTop - startScrollTop;
                  
                  // Если расстояние небольшое, прокручиваем сразу
                  if (Math.abs(distance) < 10) {
                    scrollContainer.scrollTop = desiredScrollTop;
                    setTimeout(() => {
                      this.focusField(targetElement, qSelect, qField);
                    }, 100);
                  } else {
                    const duration = 600; // миллисекунды
                    const startTime = performance.now();
                    
                    const animateScroll = (currentTime) => {
                      const elapsed = currentTime - startTime;
                      const progress = Math.min(elapsed / duration, 1);
                      
                      // Используем easing функцию для плавности
                      const easeInOutCubic = progress < 0.5
                        ? 4 * progress * progress * progress
                        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
                      
                      scrollContainer.scrollTop = startScrollTop + distance * easeInOutCubic;
                      
                      if (progress < 1) {
                        requestAnimationFrame(animateScroll);
                      } else {
                        // После завершения прокрутки фокусируемся на поле
                        setTimeout(() => {
                          this.focusField(targetElement, qSelect, qField);
                        }, 150);
                      }
                    };
                    
                    requestAnimationFrame(animateScroll);
                  }
                } else {
                  // Если не нашли контейнер, используем стандартный способ
                  targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  setTimeout(() => {
                    this.focusField(targetElement, qSelect, qField);
                  }, 400);
                }
              }
            }
          });
          return false;
        }
      }

      return true;
    },
    async onSubmit() {
      // Проверяем обязательные поля перед отправкой
      if (!this.validateRequiredFields()) {
        return;
      }

      this.loading = true
      
      const submitData = { ...this.formData }
      
      // Формируем contract_text из трех полей
      if (submitData.contract_text_type_id) {
        const selectedTextType = this.contractTextTypeOptionsWithCurrent.find(
          option => option.contract_text_type_id === submitData.contract_text_type_id ||
            String(option.contract_text_type_id) === String(submitData.contract_text_type_id)
        )
        
        if (selectedTextType) {
          const typeName = selectedTextType.contract_text_type_name;
          const nameLower = typeName.toLowerCase();
          
          // Если выбрана доверенность или талон, формируем строку с номером и датой
          if (nameLower.includes('доверенност') || nameLower.includes('талон')) {
            let contractText = typeName;
            
            // Добавляем номер, если он есть
            if (submitData.acting_basis_document_number) {
              contractText += ` №${submitData.acting_basis_document_number}`;
            }
            
            // Добавляем дату, если она есть
            if (submitData.acting_basis_document_date) {
              const formattedDate = this.formatDateForContract(submitData.acting_basis_document_date);
              if (formattedDate) {
                contractText += ` от ${formattedDate}`;
              }
            }
            
            submitData.contract_text = contractText;
          } else {
            // Для Устава просто используем название
            submitData.contract_text = typeName;
          }
        }
      }
      
      // Удаляем временные поля из отправляемых данных
      delete submitData.acting_basis_document_number;
      delete submitData.acting_basis_document_date;
      delete submitData.contract_text_type_id;
      
      await this.data?.onSubmit(submitData)
      this.loading = false
    },
    // Форматирование даты для contract_text (формат: D.MM.YYYY)
    formatDateForContract(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        
        const day = d.getDate();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        
        return `${day}.${month}.${year}`;
      } catch (e) {
        return '';
      }
    },
    // Парсинг существующего contract_text для заполнения полей
    parseContractText(contractText, options) {
      if (!contractText) return;
      
      // Пытаемся найти точное совпадение
      let foundOption = options.find(
        option => option.contract_text_type_name === contractText
      );
      
      // Если не найдено, пытаемся найти по началу строки (для случаев с номером и датой)
      if (!foundOption) {
        // Ищем опцию, которая является началом contract_text
        foundOption = options.find(option => {
          const optionName = option.contract_text_type_name.toLowerCase();
          const contractTextLower = contractText.toLowerCase();
          return contractTextLower.startsWith(optionName);
        });
      }
      
      if (foundOption) {
        this.formData.contract_text_type_id = foundOption.contract_text_type_id;
        
        // Парсим номер и дату, если они есть
        const typeName = foundOption.contract_text_type_name;
        const nameLower = typeName.toLowerCase();
        
        // Если это доверенность или талон, парсим номер и дату
        if (nameLower.includes('доверенност') || nameLower.includes('талон')) {
          // Ищем номер после "№"
          const numberMatch = contractText.match(/№\s*([^\s]+)/);
          if (numberMatch) {
            this.formData.acting_basis_document_number = numberMatch[1];
          }
          
          // Ищем дату после "от"
          const dateMatch = contractText.match(/от\s+(\d{1,2}\.\d{2}\.\d{4})/);
          if (dateMatch) {
            // Преобразуем дату из формата D.MM.YYYY в формат для date picker
            const dateParts = dateMatch[1].split('.');
            if (dateParts.length === 3) {
              const year = dateParts[2];
              const month = dateParts[1];
              const day = dateParts[0];
              // Формат для date picker: YYYY-MM-DD
              this.formData.acting_basis_document_date = `${year}-${month}-${day}`;
            }
          }
        }
      } else {
        // Если значение не найдено в списке, создаем временный ID для отображения
        this.formData.contract_text_type_id = `temp_${contractText}`;
      }
    },
    async fetchCities() {
      this.cityOptions = await getCity();
    },
    async fetchTaxoparks() {
      this.taxoparkOptions = await getTaxopark();
    },
    async fetchContractorType() {
      this.contractorTypeOptions = await getContractorType();
    },
    async fetchContragentType() {
      this.contragentTypeOptions = await getContragentType();
    },
    async fetchContractorMainType() {
      this.contractorMainTypeOptions = await getContractorMainType();
    },
    async fetchSBSsnr() {
      this.sbsSnrOptions = await getSBSsnr();
    },
    async fetchContractTextType() {
      this.contractTextTypeOptions = await getContractTextType();
    },
    async contragentSBSFilterFunc(value) {
      if (!value) {
        this.formData.sbs_company_id = '';
        this.formData.sbs_company_name = '';
        this.contragentSBSOptions = [];
        this.contragentSBSLoading = false;
        return;
      }

      this.formData.sbs_company_name = value;

      if (this.sbsCompanySearchTimeout) {
        clearTimeout(this.sbsCompanySearchTimeout);
      }

      if (value.length >= 3) {
        this.contragentSBSLoading = true;

        this.sbsCompanySearchTimeout = setTimeout(async () => {
          let body = { search_text: value };
          const response = await mapSBSCompany(body);
          this.contragentSBSOptions = response || [];
          this.contragentSBSLoading = false;
        }, 300);
      } else {
        this.contragentSBSOptions = [];
        this.contragentSBSLoading = false;
      }
    },
    async partnerSBSFilterFunc(value) {
      this.formData.sbs_partner_name = value;

      if (!value) {
        this.formData.sbs_partner_id = '';
        this.formData.sbs_partner_name = '';
        this.partnerSBSOptions = [];
        this.partnerSBSLoading = false;
        return;
      }

      if (this.sbsPartnerSearchTimeout) {
        clearTimeout(this.sbsPartnerSearchTimeout);
      }

      if (value?.length >= 3) {
        this.partnerSBSLoading = true;

        this.sbsPartnerSearchTimeout = setTimeout(async () => {
          let body = { search_text: value }
          const response = await mapSBSPartner(body);
          this.partnerSBSOptions = response || [];
          this.partnerSBSLoading = false;
        }, 300);
      } else {
        this.partnerSBSOptions = [];
        this.partnerSBSLoading = false;
      }
    },
    sbsVidyDeyatelnostiSelectItem(value, obj) {
      if (!obj || obj === null || obj === undefined) {
        this.formData = {
          ...this.formData,
          sbs_vidy_deyatelnosti_id: null,
          display_name: ''
        };
      } else {
        this.formData = {
          ...this.formData,
          sbs_vidy_deyatelnosti_id: obj.sbs_vidy_deyatelnosti_id,
          display_name: obj.display_name
        };
      }
    },
    async sbsVidyDeyatelnostiFilterFunc(val, update) {
      update(async (value) => {
        if (!val || val.trim() === '') {
          this.sbsVidyDeyatelnostiLoading = true
          let body = { search_text: '' }
          const res = await mapSBSVidyDeyatelnosti(body);
          this.sbsVidyDeyatelnostiLoading = false;
          this.sbsVidyDeyatelnostiOptions = res || [];
          return;
        }
        this.sbsVidyDeyatelnostiLoading = true
        let body = { search_text: val }
        const res = await mapSBSVidyDeyatelnosti(body);
        this.sbsVidyDeyatelnostiLoading = false;
        this.sbsVidyDeyatelnostiOptions = res || [];
      });
    },
    sbsVidyDeyatelnostiChange(value, row) {
      if (!row || !value || !row.display_name) {
        // При очистке выбора - оставляем ограниченный список, если подрядчик был выбран с несколькими видами
        this.formData = {
          ...this.formData,
          sbs_vidy_deyatelnosti_id: null,
          display_name: ''
        };
        // Если у подрядчика несколько видов деятельности, показываем предупреждение снова
        if (this.hasMultipleOkedyFromPartner) {
          this.showMultipleOkedyWarning = true;
        }
      } else {
        this.formData = {
          ...this.formData,
          sbs_vidy_deyatelnosti_id: row.sbs_vidy_deyatelnosti_id,
          display_name: row.display_name
        };
        // Скрываем предупреждение, когда пользователь выбрал вид деятельности
        this.showMultipleOkedyWarning = false;
        // НЕ очищаем ограниченный список и флаг, чтобы при очистке выбора вернулся ограниченный список
        // Ограниченный список нужен для правильного отображения выбранного значения
      }
    },
    async loadSBSVidyDeyatelnosti() {
      this.sbsVidyDeyatelnostiLoading = true
      let body = { search_text: '' }
      const res = await mapSBSVidyDeyatelnosti(body);
      this.sbsVidyDeyatelnostiLoading = false
      this.sbsVidyDeyatelnostiOptions = res || [];
      if (this.formData.sbs_vidy_deyatelnosti_id && this.data?.body?.display_name) {
        const exists = this.sbsVidyDeyatelnostiOptions.some(opt => opt.sbs_vidy_deyatelnosti_id === this.formData.sbs_vidy_deyatelnosti_id);
        if (!exists) {
          this.sbsVidyDeyatelnostiOptions.unshift({
            sbs_vidy_deyatelnosti_id: this.formData.sbs_vidy_deyatelnosti_id,
            display_name: this.data.body.display_name,
            kod_oked: this.data.body.kod_oked,
            naimenovanie: this.data.body.naimenovanie_oked,
            guid_oked_for_onec: this.data.body.guid_oked_for_onec
          });
        }
      }
    },
  },
  mounted() {
    this.fetchCities();
    this.$watch('cityOptions', (newOptions) => {
      if (newOptions.length && !this.formData.city_id) {
        this.formData.city_id = newOptions[0].city_id;
      }
    });
    this.fetchTaxoparks();
    this.$watch('taxoparkOptions', (newOptions) => {
      if (newOptions.length && !this.formData.taxopark_id) {
        this.formData.taxopark_id = newOptions[0].taxopark_id;
      }
    });
    this.fetchContractorType();
    this.fetchContragentType();
    this.fetchContractorMainType();
    this.fetchSBSsnr();
    this.fetchContractTextType();
    this.$watch('contractTextTypeOptions', (newOptions) => {
      // Если есть contract_text, но нет contract_text_type_id, парсим его
      if (newOptions.length && this.formData.contract_text && !this.formData.contract_text_type_id) {
        this.parseContractText(this.formData.contract_text, newOptions);
      }
    });
    this.loadSBSVidyDeyatelnosti();
    // Ограничим варианты СНР/ОКЭД на старте, если уже есть привязка к SBS и выбор не сделан
    this.initSbsFromLinkedPartner();
  },
  beforeUnmount() {
    if (this.sbsCompanySearchTimeout) {
      clearTimeout(this.sbsCompanySearchTimeout);
    }
    if (this.sbsPartnerSearchTimeout) {
      clearTimeout(this.sbsPartnerSearchTimeout);
    }
  }
}
</script>

<style scoped lang="scss">
.form-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  &__title {
    margin: 0 0 16px 0;
    padding-bottom: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #1976d2;
    border-bottom: 2px solid #e3f2fd;
  }
}

.form {
  display: grid;
  flex-wrap: wrap;
  grid-template-columns: 6fr 6fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 12fr;
  }

  &__item {
    flex: 1 1 45%;

    &--full {
      grid-column: 1 / -1;
    }
  }
}

.okedy-warning-text {
  margin-top: 8px;
  font-size: 12px;
  color: #d32f2f;
  line-height: 1.4;
  padding: 4px 0;
}
</style>
